import React, {useEffect, useRef, useState} from 'react';
import {View, Platform, StyleProp, ViewStyle} from 'react-native';
import CCText from '@/Atoms/CCText';
import {Popover, ArrowContainer} from 'react-tiny-popover';

const Tooltip = ({
  children,
  text,
  /** Rich popover content; when set, replaces the default `text` label. */
  body,
  position = 'top',
  backgroundColor,
  textcolor,
  borderColor,
  arrowColor,
  fontSize,
  width,
  height,
  paddingVertical,
  borderWidth,
  contentStyle,
}: {
  children: any;
  text?: string;
  body?: React.ReactNode;
  position?: string;
  backgroundColor?: string;
  textcolor?: string;
  borderColor?: string;
  arrowColor?: string;
  fontSize?: number;
  width?: number;
  height?: number;
  paddingVertical?: number;
  borderWidth?: number;
  contentStyle?: StyleProp<ViewStyle>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const openTimestampRef = useRef<number | null>(null);

  function toggle() {
    setIsOpen(prev => {
      const nextState = !prev;
      if (nextState) {
        openTimestampRef.current = Date.now(); // mark open time
      } else {
        openTimestampRef.current = null;
      }
      return nextState;
    });
  }

  // Safety check effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) {
      timer = setTimeout(() => {
        if (openTimestampRef.current) {
          const elapsed = Date.now() - openTimestampRef.current;
          if (elapsed >= 10000) {
            setIsOpen(false); // force close if > 5s
            openTimestampRef.current = null;
          }
        }
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [isOpen]);

  const popoverContainerStyle: ViewStyle = {
    backgroundColor: backgroundColor || '#000',
    borderRadius: 10,
    paddingVertical: paddingVertical ?? 12,
    paddingHorizontal: 16,
    maxWidth: width || 300,
    height: height || undefined,
    borderWidth: borderWidth ?? 1,
    borderColor: borderColor || '#000',
    borderStyle: 'solid',
  };

  const content = ({position: arrowPosition, childRect, popoverRect}) => (
    <ArrowContainer
      position={arrowPosition}
      childRect={childRect}
      popoverRect={popoverRect}
      arrowColor={arrowColor || '#000'}
      arrowSize={8}>
      <View style={[popoverContainerStyle, contentStyle]}>
        {body != null ? (
          body
        ) : (
          <CCText color={textcolor || '#fff'} fontSize={fontSize || 13}>
            {text}
          </CCText>
        )}
      </View>
    </ArrowContainer>
  );

  return (
    <Popover
      isOpen={isOpen}
      positions={[position]}
      align="center"
      padding={8}
      reposition={true}
      onClickOutside={() => setIsOpen(false)}
      containerStyle={{zIndex: 10000}}
      containerClassName="tooltip-container"
      container={Platform.OS === 'web' ? document.body : undefined}
      content={content}>
      {typeof children === 'function' ? children?.(toggle) : <></>}
    </Popover>
  );
};

export default Tooltip;
